// Profile credit card (customer or company): shows the credit balance + ledger
// history and lets staff refund credit as cash. Credit is the ledger in
// client_credit_ledger — money explicitly moved up from reservations.
import React, { useEffect, useState, useCallback } from "react";
import {
  Card, BlockStack, InlineStack, Text, Button, Collapsible,
  Modal, TextField, Select, Spinner,
} from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import supabase from "../../../supabase-client";
import { UserAuth } from "../../../context/AuthContext";
import { formatPrice, formatFriendlyDate } from "../../../utility/funcs";
import { getCreditBalance, getCreditLedger, refundCredit } from "../../../utility/clientCredit";

const REASON_LABEL = {
  overpayment: "Excedente de reservación",
  applied: "Aplicado a reservación",
  refund: "Reembolso",
  cancellation: "Cancelación de reservación",
  deposit: "Depósito no devuelto",
};

function ClientCreditCard({ entityType, entityId, refreshKey }) {
  const { session } = UserAuth();
  const [balance, setBalance] = useState(0);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [tick, setTick] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [refundModal, setRefundModal] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundAccount, setRefundAccount] = useState("");
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(async () => {
    if (!entityId) { setLoading(false); return; }
    setLoading(true);
    const [b, l] = await Promise.all([
      getCreditBalance(entityType, entityId),
      getCreditLedger(entityType, entityId),
    ]);
    setBalance(b);
    setLedger(l);
    setLoading(false);
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load, refreshKey, tick]);
  // Payout methods. Excludes the internal "Crédito de cliente" account —
  // refunding credit back into credit is a no-op.
  useEffect(() => {
    supabase.from("transaction_accounts").select("id, name, type").neq("type", "credit").eq("manual_entry", true).order("order")
      .then(({ data }) => {
        setAccounts(data || []);
        const def = (data || []).find((a) => a.type === "bank") || (data || [])[0];
        if (def) setRefundAccount(String(def.id));
      });
  }, []);

  // Hide entirely when the client has no credit (don't show a $0 card).
  if (!entityId || loading || balance <= 0.005) return null;

  const doRefund = async () => {
    setRefunding(true);
    const res = await refundCredit({
      entityType, entityId, amount: parseFloat(refundAmount) || 0,
      accountId: refundAccount, createdBy: session?.user?.id,
    });
    setRefunding(false);
    if (!res.success) { alert(res.error); return; }
    setRefundModal(false);
    setRefundAmount("");
    setTick((t) => t + 1);
  };

  return (
    <div style={{ marginTop: "2rem" }}>
      <Card>
        <BlockStack gap="300">
          <InlineStack align="space-between" blockAlign="center">
            <Text variant="headingMd">Crédito del cliente</Text>
            <InlineStack gap="200" blockAlign="center">
              {loading ? <Spinner size="small" /> : (
                <Text variant="headingLg" tone={balance > 0.005 ? "success" : undefined}>
                  {formatPrice(balance)}
                </Text>
              )}
              {ledger.length > 0 && (
                <Button variant="plain" icon={expanded ? ChevronUpIcon : ChevronDownIcon}
                  onClick={() => setExpanded((e) => !e)} accessibilityLabel="Ver historial" />
              )}
            </InlineStack>
          </InlineStack>

          {balance > 0.005 && (
            <div>
              <Button onClick={() => { setRefundAmount(String(balance)); setRefundModal(true); }}>
                Reembolsar
              </Button>
            </div>
          )}

          <Collapsible open={expanded} id="credit-ledger" transition={{ duration: "150ms" }}>
            <BlockStack gap="150">
              {ledger.map((e) => (
                <InlineStack key={e.id} align="space-between" blockAlign="center" gap="200">
                  <BlockStack gap="050">
                    <Text variant="bodySm">
                      {REASON_LABEL[e.reason] || e.reason}
                      {e.source_reservation_id ? ` · res #${e.source_reservation_id}`
                        : e.target_reservation_id ? ` · res #${e.target_reservation_id}` : ""}
                    </Text>
                    <Text variant="bodySm" tone="subdued">{formatFriendlyDate(e.created_at)}</Text>
                  </BlockStack>
                  <Text variant="bodySm" tone={e.amount > 0 ? "success" : "critical"}>
                    {e.amount > 0 ? "+" : ""}{formatPrice(e.amount)}
                  </Text>
                </InlineStack>
              ))}
            </BlockStack>
          </Collapsible>
        </BlockStack>
      </Card>

      <Modal
        open={refundModal}
        onClose={() => setRefundModal(false)}
        title="Reembolsar crédito"
        primaryAction={{ content: "Reembolsar", onAction: doRefund, loading: refunding, disabled: refunding || !refundAccount || !(parseFloat(refundAmount) > 0) }}
        secondaryActions={[{ content: "Cancelar", onAction: () => setRefundModal(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text tone="subdued">Crédito disponible: {formatPrice(balance)}</Text>
            <TextField label="Monto" type="number" prefix="$" value={refundAmount} onChange={setRefundAmount} autoComplete="off" />
            {(() => {
              // Collapse bank accounts (Afirme, Base, …) into a single
              // "Transferencia SPEI" option — staff doesn't pick which bank;
              // that's finalized when the transfer is actually sent. Mirrors
              // the deposit-return and reservation-refund modals.
              const banks = accounts.filter((a) => a.type === "bank");
              const nonBanks = accounts.filter((a) => a.type !== "bank");
              const selectedIsBank = banks.some((b) => String(b.id) === refundAccount);
              const speiId = selectedIsBank ? refundAccount : banks[0]?.id;
              const options = [
                ...nonBanks.map((a) => ({ label: a.name, value: String(a.id) })),
                ...(banks.length > 0
                  ? [{ label: "Transferencia SPEI", value: String(speiId) }]
                  : []),
              ];
              return (
                <Select
                  label="Reembolsar mediante"
                  options={options}
                  value={refundAccount}
                  onChange={setRefundAccount}
                  helpText="Efectivo y tarjeta se marcan como reembolsados automáticamente; SPEI queda pendiente de transferir."
                />
              );
            })()}
            <Text tone="subdued" variant="bodySm">Registra la salida del dinero y reduce el crédito.</Text>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </div>
  );
}

export default ClientCreditCard;
